import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subscription, from, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onIdTokenChanged,
  Auth,
  User,
} from 'firebase/auth';

import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
  private app: FirebaseApp;
  private auth: Auth;
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private authReadySubject = new BehaviorSubject<boolean>(false);
  private refreshTimerSub: Subscription | null = null;

  token$ = this.tokenSubject.asObservable();
  authReady$ = this.authReadySubject.asObservable();

  constructor(private ngZone: NgZone) {
    this.app = initializeApp(environment.firebaseConfig);
    this.auth = getAuth(this.app);
    if (environment.firebaseTenantId) {
      this.auth.tenantId = environment.firebaseTenantId;
    }

    onIdTokenChanged(this.auth, (user: User | null) => {
      this.ngZone.run(async () => {
        if (user) {
          const token = await user.getIdToken();
          this.tokenSubject.next(token);
          this.scheduleTokenRefresh(token);
        } else {
          this.tokenSubject.next(null);
          this.clearRefreshTimer();
        }
        if (!this.authReadySubject.value) {
          this.authReadySubject.next(true);
        }
      });
    });
  }

  private scheduleTokenRefresh(token: string): void {
    this.clearRefreshTimer();
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const delay = Math.max(expiresAt - Date.now() - 5 * 60 * 1000, 0);
      this.refreshTimerSub = timer(delay).pipe(
        switchMap(() => from(this.getIdToken(true)))
      ).subscribe();
    } catch {
      // If token can't be decoded, skip scheduling
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerSub) {
      this.refreshTimerSub.unsubscribe();
      this.refreshTimerSub = null;
    }
  }

  signIn(email: string, password: string): Promise<string> {
    return signInWithEmailAndPassword(this.auth, email, password)
      .then((credential) => credential.user.getIdToken());
  }

  getIdToken(forceRefresh = false): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return Promise.resolve(null);
    return user.getIdToken(forceRefresh);
  }

  signOut(): Promise<void> {
    return signOut(this.auth);
  }

  get currentUser(): User | null {
    return this.auth.currentUser;
  }

  authReady(): Promise<string | null> {
    return new Promise((resolve) => {
      const unsubscribe = onIdTokenChanged(this.auth, async (user) => {
        unsubscribe();
        if (user) {
          const token = await user.getIdToken();
          resolve(token);
        } else {
          resolve(null);
        }
      });
    });
  }
}
